import { useMatch } from "react-router";
import { z } from "zod";

import {
  cardSchema,
  CardKind,
  groupBySchema,
  Placement,
  SERIES_PALETTE_SIZE,
} from "@/shared/domain/dashboards/DashboardDefinition";
import {
  CHART_DISPLAY_TYPES,
  ChartDisplayType,
  NUMERIC_AGGREGATIONS,
  NumericAggregation,
} from "@/shared/domain/metrics";
import { metricsWidgetRoutePatterns } from "@/shared/navigation";

import { CardConfig } from "../DashboardHeader/DashboardParams";

/**
 * URL search-param keys carrying the widget being configured — the whole
 * draft, not just the cell it was opened on.
 *
 * A routed editor that kept its draft in component state would lose every
 * choice on reload while still paying the full price of a public URL:
 * parsing, degradation, and a shape stale links can hit. Carrying the draft
 * is what that price buys.
 *
 * Query params rather than path segments because they are all optional — the
 * editor opens perfectly well without any of them (see
 * {@link readWidgetDraft}), which is what makes a hand-typed `/widgets/new` a
 * working URL rather than a 404. Identity stays in the path: which dashboard,
 * and which card.
 */
export const WIDGET_DRAFT_PARAMS = {
  section: "section",
  row: "row",
  col: "col",
  width: "w",
  height: "h",
  kind: "kind",
  metric: "metric",
  title: "title",
  aggregation: "aggregation",
  display: "display",
  color: "color",
  group_by: "group_by",
} as const satisfies Record<DraftedField, string>;

/** A URL param a draft is written to. */
type DraftParam =
  (typeof WIDGET_DRAFT_PARAMS)[keyof typeof WIDGET_DRAFT_PARAMS];

/** What a chart or trend falls back to when nothing readable says otherwise.
 *  Shared with `WidgetFields`, so the value the URL degrades to and the value
 *  its pickers restore are the same one. */
export const DEFAULT_AGGREGATION: NumericAggregation = "mean";
export const DEFAULT_DISPLAY: ChartDisplayType = "line";

/**
 * The displays this chart variant can draw.
 *
 * A raw chart draws on a TIME-PROPORTIONAL x-axis, which Chart.js cannot size
 * bars against (see `CHART_DISPLAY_TYPES.linearAxis`) — so bar is left out
 * there rather than offered as a choice that renders hairlines. Everything
 * else is offered on both: line and scatter position their marks pointwise
 * and do not care how the axis is laid out.
 */
export function displayOptionsFor(
  isRawChart: boolean,
): readonly { value: ChartDisplayType; label: string }[] {
  return isRawChart
    ? CHART_DISPLAY_TYPES.filter((display) => display.linearAxis)
    : CHART_DISPLAY_TYPES;
}

/**
 * What a RAW card PERSISTS for `display`, or `undefined` to write no key at
 * all.
 *
 * The default line stays unwritten, exactly as the absent aggregation does: a
 * raw card saved before this field existed carries no display, and writing a
 * defaulted one would read every such dashboard back as dirty. A bar its axis
 * cannot draw is likewise not persisted — the card would render the line it
 * degrades to, so the honest record of that is no display at all (the editor
 * still remembers the bar for a switch back to aggregated).
 */
export function rawDisplay(
  display: ChartDisplayType,
): ChartDisplayType | undefined {
  const offered = displayOptionsFor(true).some(
    (option) => option.value === display,
  );
  return offered && display !== DEFAULT_DISPLAY ? display : undefined;
}

/** Every key of every card variant — `keyof` a union would give only the
 *  keys the variants share. */
type KeysOfUnion<T> = T extends unknown ? keyof T : never;

/**
 * What the URL has to carry for a draft to survive a reload: every card field,
 * with `placement` spelled as its four numbers and `id` left to the path.
 *
 * Checked against {@link WIDGET_DRAFT_PARAMS} so a field added to the card
 * schema is a compile error here until the URL learns it. The draft is read
 * back from the URL on every change, so a field it cannot carry is not merely
 * lost on reload — it is dropped the moment anything else is edited.
 */
type DraftedField =
  | Exclude<KeysOfUnion<CardConfig>, "id" | "placement">
  | "row"
  | "col"
  | "width"
  | "height";

/**
 * The id a not-yet-committed widget carries.
 *
 * A placeholder, not an identity: the real id is minted at commit
 * (`generateCardId`), so a create draft that is reloaded, abandoned, or
 * committed twice can never collide with a card already on the canvas. It is
 * deliberately absent from {@link WIDGET_DRAFT_PARAMS} — a draft has no
 * identity worth carrying in a URL.
 */
export const NEW_WIDGET_ID = "new-widget";

/**
 * A fresh widget with no metric picked yet — what the editor shows when the
 * URL says nothing about the one being added.
 *
 * `placement` comes from the canvas, which fitted it to the gap the user
 * clicked at the moment they clicked it; its absence auto-flows into the
 * first gap that fits. Master-grid coordinates, so a resize between opening
 * the editor and committing cannot invalidate them.
 */
export function defaultWidgetDraft(opts: {
  kind?: CardKind;
  section: string;
  placement?: Placement;
}): CardConfig {
  // Keys in schema order, like every other card this app builds: the
  // dashboard's dirty check compares stringified definitions.
  const placed = {
    ...(opts.placement !== undefined ? { placement: opts.placement } : {}),
    section: opts.section,
  };
  return opts.kind === "trend"
    ? {
        id: NEW_WIDGET_ID,
        kind: "trend",
        metric: null,
        aggregation: DEFAULT_AGGREGATION,
        ...placed,
      }
    : {
        id: NEW_WIDGET_ID,
        kind: "chart",
        metric: null,
        aggregation: DEFAULT_AGGREGATION,
        display: DEFAULT_DISPLAY,
        ...placed,
      };
}

/** The enum vocabularies the card schema itself ranges over, reused rather
 *  than restated so a param can never accept a value a card cannot hold. */
const aggregationParam = z.enum(NUMERIC_AGGREGATIONS);
const displayParam = z.enum(
  CHART_DISPLAY_TYPES.map((display) => display.value),
);
// A palette SLOT, so digits — but `z.coerce.number()` would turn the absent
// param's `null` into 0, silently making "no colour chosen" read as slot 0.
// Matching the string first means an absent or malformed param fails to parse
// and `readEnum` reports it as the absence it is.
const colorParam = z
  .string()
  .regex(/^\d+$/)
  .transform(Number)
  .pipe(z.int().max(SERIES_PALETTE_SIZE - 1));

function readKind(raw: string | null): CardKind {
  return raw === "trend" || raw === "chart" ? raw : "chart";
}

/** A 1-based grid coordinate, or `null` for anything that isn't one. */
function readCoordinate(raw: string | null): number | null {
  if (raw === null) {
    return null;
  }
  const value = Number(raw);
  return Number.isInteger(value) && value >= 1 ? value : null;
}

/** A param's value if the vocabulary accepts it, `null` if it is absent or
 *  unreadable — so one bad field degrades alone. */
function readEnum<S extends z.ZodType>(
  schema: S,
  raw: string | null,
): z.infer<S> | null {
  const parsed = schema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/**
 * A placement needs ALL FOUR of its numbers: three quarters of a rectangle is
 * not a position, so a partial one auto-flows rather than guessing the rest.
 */
function readPlacement(params: URLSearchParams): Placement | null {
  const row = readCoordinate(params.get(WIDGET_DRAFT_PARAMS.row));
  const col = readCoordinate(params.get(WIDGET_DRAFT_PARAMS.col));
  const w = readCoordinate(params.get(WIDGET_DRAFT_PARAMS.width));
  const h = readCoordinate(params.get(WIDGET_DRAFT_PARAMS.height));
  return row !== null && col !== null && w !== null && h !== null
    ? { row, col, w, h }
    : null;
}

/** A trimmed param value, or `null` for absent-or-blank. */
function readText(params: URLSearchParams, key: string): string | null {
  const value = params.get(key)?.trim() ?? "";
  return value === "" ? null : value;
}

/** Whether the query says anything at all about the widget. */
function carriesDraft(params: URLSearchParams): boolean {
  return Object.values(WIDGET_DRAFT_PARAMS).some((key) => params.has(key));
}

/** A raw chart's `display` as a spreadable fragment: the key, or nothing at
 *  all. Mirrors the `...(x !== null ? {x} : {})` shape the rest of
 *  {@link readWidgetDraft} is written in. */
function rawDisplayFields(display: ChartDisplayType): {
  display?: ChartDisplayType;
} {
  const drawn = rawDisplay(display);
  return drawn === undefined ? {} : { display: drawn };
}

/**
 * The widget the URL describes, over `base` — the card being reconfigured, or
 * a default draft when one is being added.
 *
 * A query carrying NO draft params is not a draft, it is an unqualified
 * address: `base` comes back untouched, so a deep link to
 * `/widgets/:cardId/edit` opens the card as saved rather than as a blank one.
 * The moment any draft param appears, the query IS the draft and describes it
 * completely — which is what lets a chart the user switched to "all sessions"
 * come back raw on reload rather than silently re-acquiring an aggregation.
 *
 * Every field degrades independently, so a URL that has been hand-edited,
 * truncated by a link shortener, or built against an older param spelling
 * still opens the editor: an unrecognized kind is a chart, a partial placement
 * auto-flows, an unreadable colour or grouping is none, a blank title is the
 * generated one, and a missing metric is the nullable metric the schema
 * already allows. There is nothing the user could do with an error message
 * here that opening the editor does not do better.
 *
 * `cardSchema` — the same validator the stored definition passes through — has
 * the last word, so the URL boundary can never admit a card the storage
 * boundary would reject.
 */
export function readWidgetDraft(
  params: URLSearchParams,
  base: CardConfig,
): CardConfig {
  if (!carriesDraft(params)) {
    return base;
  }

  const placement = readPlacement(params);
  const section = readText(params, WIDGET_DRAFT_PARAMS.section);
  const title = readText(params, WIDGET_DRAFT_PARAMS.title);
  const common = {
    id: base.id,
    metric: readText(params, WIDGET_DRAFT_PARAMS.metric),
    ...(title !== null ? { title } : {}),
    ...(placement !== null ? { placement } : {}),
    ...(section !== null ? { section } : {}),
  };

  const aggregation = readEnum(
    aggregationParam,
    params.get(WIDGET_DRAFT_PARAMS.aggregation),
  );
  const display = readEnum(
    displayParam,
    params.get(WIDGET_DRAFT_PARAMS.display),
  );
  const color = readEnum(colorParam, params.get(WIDGET_DRAFT_PARAMS.color));
  const groupBy = readEnum(
    groupBySchema,
    params.get(WIDGET_DRAFT_PARAMS.group_by),
  );

  const candidate =
    readKind(params.get(WIDGET_DRAFT_PARAMS.kind)) === "trend"
      ? // A trend tile always aggregates; there is no variant of it that
        // doesn't, so an unreadable aggregation falls back rather than
        // changing what the card is.
        {
          ...common,
          kind: "trend",
          aggregation: aggregation ?? DEFAULT_AGGREGATION,
        }
      : {
          ...common,
          kind: "chart",
          // A chart's aggregation is what distinguishes its two variants, so
          // its ABSENCE is meaningful: no aggregation is the raw chart.
          //
          // Both variants carry a display; they differ in what they can draw
          // and in what they bother to write down. An aggregated card always
          // names one, defaulting when the param is missing or unreadable. A
          // raw card only records a display its axis can draw and that the
          // default cannot be inferred from — see {@link rawDisplay} — so the
          // key is simply absent for the line every raw card drew before this
          // param existed.
          ...(aggregation !== null
            ? { aggregation, display: display ?? DEFAULT_DISPLAY }
            : rawDisplayFields(display ?? DEFAULT_DISPLAY)),
          ...(color !== null ? { color } : {}),
          ...(groupBy !== null ? { group_by: groupBy } : {}),
        };

  const parsed = cardSchema.safeParse(candidate);
  return parsed.success ? parsed.data : base;
}

/**
 * The `mergeParams` patch that records `card` in the URL. Nulls clear the
 * keys, so every write describes the whole draft: switching a chart to raw or
 * clearing its metric cannot leave the previous value behind for the next
 * reader to pick up.
 */
export function writeWidgetDraft(
  card: CardConfig,
): Record<DraftParam, string | null> {
  const placement = card.placement ?? null;
  return {
    [WIDGET_DRAFT_PARAMS.section]: card.section ?? null,
    [WIDGET_DRAFT_PARAMS.row]: placement ? String(placement.row) : null,
    [WIDGET_DRAFT_PARAMS.col]: placement ? String(placement.col) : null,
    [WIDGET_DRAFT_PARAMS.width]: placement ? String(placement.w) : null,
    [WIDGET_DRAFT_PARAMS.height]: placement ? String(placement.h) : null,
    [WIDGET_DRAFT_PARAMS.kind]: card.kind,
    [WIDGET_DRAFT_PARAMS.metric]: card.metric,
    [WIDGET_DRAFT_PARAMS.title]: card.title ?? null,
    [WIDGET_DRAFT_PARAMS.aggregation]: card.aggregation ?? null,
    [WIDGET_DRAFT_PARAMS.display]:
      card.kind === "chart" ? card.display ?? null : null,
    [WIDGET_DRAFT_PARAMS.color]:
      card.kind === "chart" && card.color !== undefined
        ? String(card.color)
        : null,
    [WIDGET_DRAFT_PARAMS.group_by]:
      card.kind === "chart" ? card.group_by ?? null : null,
  };
}

/**
 * The `mergeParams` patch that removes every draft key. A draft describes a
 * widget that is being configured; leaving it in the URL after the editor
 * closes would let a stale one seed the next widget, or show up in a shared
 * link to a dashboard that has no editor open.
 */
export function clearWidgetDraft(): Record<string, null> {
  return Object.fromEntries(
    Object.values(WIDGET_DRAFT_PARAMS).map((key) => [key, null]),
  );
}

/**
 * Are we creating or editing a new widget
 */
export type WidgetEditorMode =
  | { kind: "create" }
  | { kind: "edit"; cardId: string };

/**
 * Derive the editor mode from the URL.
 *
 * The mode is read from the path rather than held in state so that the editor
 * survives a reload, is reachable by deep link, and closes on the back button
 * — the three things the popovers it replaces could not do.
 *
 * Each door has two patterns: one naming the dashboard the widget belongs to,
 * one for the unsaved view, which has no record to name. Both are matched
 * here so the editor behaves identically either side of a first save.
 *
 * `deviceScoped` picks which set of patterns to match: `MetricsDashboard`
 * mounts under both `/metrics/...` and `/devices/:deviceId/metrics/...`, and
 * its `deviceId` prop is exactly that distinction.
 */
export function useWidgetEditorMode(opts: {
  deviceScoped: boolean;
}): WidgetEditorMode | null {
  const patterns = metricsWidgetRoutePatterns({
    deviceScoped: opts.deviceScoped,
  });
  const createMatch = useMatch(patterns.create);
  const createWithoutDashboardMatch = useMatch(patterns.createWithoutDashboard);
  const editMatch = useMatch(patterns.edit);
  const editWithoutDashboardMatch = useMatch(patterns.editWithoutDashboard);

  const cardId =
    editMatch?.params.cardId ?? editWithoutDashboardMatch?.params.cardId;
  if (cardId !== undefined) {
    return { kind: "edit", cardId };
  }
  if (createMatch !== null || createWithoutDashboardMatch !== null) {
    return { kind: "create" };
  }
  return null;
}
